import React from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Linking,
  Image,
} from 'react-native';
import { Text } from '@/components/common/text';
import { showConfirmWithButtons } from '@/utils/common/alert';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Icon from 'react-native-vector-icons/Ionicons';
import { useAppSelector } from '@/store';
import { RootStackParamList } from '@/navigation/RootNavigator';
import {
  PADDING,
  TYPOGRAPHY,
  ICON_SIZES,
} from '@/constants/residents/homeConstants';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { homeStrings } from '@/constants/residents';
import { formatAsPhoneNumber } from '@/utils/residents/utils';

type ContactOfficeNavigationProp = StackNavigationProp<RootStackParamList, 'ContactOffice'>;

interface ContactInfoItemProps {
  icon: string;
  title?: string;
  subtitle?: string;
  onPress?: () => void;
  showArrow?: boolean;
  isLast?: boolean;
}

const ContactInfoItem: React.FC<ContactInfoItemProps> = ({
  icon,
  title,
  subtitle,
  onPress,
  showArrow = false,
  isLast = false,
}) => {
  const styles = useThemedStyles(makeStyles);
  const { semantic } = useThemeColors();
  const Component = onPress ? TouchableOpacity : View;
  
  return (
    <Component
      style={[styles.infoItem, !onPress && styles.infoItemDisabled]}
      onPress={onPress}
      // @ts-ignore - TouchableOpacity props
      disabled={!onPress}
    >
      <View style={styles.infoIconContainer}>
        <Icon name={icon} size={ICON_SIZES.medium} color={semantic.text.link} />
      </View>
      <View style={styles.infoContent}>
        {title && <Text style={styles.infoTitle}>{title}</Text>}
        {subtitle && <Text style={styles.infoSubtitle}>{subtitle}</Text>}
      </View>
      {showArrow && (
        <Icon name="chevron-forward" size={ICON_SIZES.small} color={semantic.icon.muted} />
      )}
      {/* Separator like iOS ContactOfficeInfoViewCell */}
      {!isLast && <View style={styles.separator} />}
    </Component>
  );
};

export const ContactOfficeScreen: React.FC = () => {
  const styles = useThemedStyles(makeStyles);
  const navigation = useNavigation<ContactOfficeNavigationProp>();
  const { user } = useAppSelector((state) => state.auth);
  const property = user?.property;

  // Set navigation title like iOS
  React.useEffect(() => {
    navigation.setOptions({ 
      title: homeStrings.propertyInfo.title 
    });
  }, [navigation]);

  const handleWebsitePress = (url: string) => {
    navigation.navigate('WebView', { url, title: url });
  };

  const handlePhonePress = (phone: string) => {
    // Show Call/Text options
    showConfirmWithButtons(
      'Contact',
      'How would you like to contact?',
      'Call',
      'Text',
      () => {
        const phoneUrl = `tel:${phone}`;
        Linking.canOpenURL(phoneUrl).then((supported) => {
          if (supported) {
            Linking.openURL(phoneUrl);
          }
        });
      },
      () => {
        const smsUrl = `sms:${phone}`;
        Linking.canOpenURL(smsUrl).then((supported) => {
          if (supported) {
            Linking.openURL(smsUrl);
          }
        });
      }
    );
  };

  const handleEmailPress = (email: string) => {
    const emailUrl = `mailto:${email}`;
    Linking.canOpenURL(emailUrl).then((supported) => {
      if (supported) {
        Linking.openURL(emailUrl);
      }
    });
  };

  const handleHandbookPress = (url: string) => {
    navigation.navigate('WebView', { url, title: homeStrings.faqs.title });
  };

  const handleResidentHandbookPress = () => {
    // Navigate to FAQ WebView (matching iOS showFAQ)
    // Only called when handbook URL exists (see buildSections check)
    const faqUrl = property?.faqUrl || property?.websites?.handbook;
    if (faqUrl) {
      navigation.navigate('WebView', { 
        url: faqUrl, 
        title: homeStrings.faqs.title 
      });
    }
  };

  // Format address string like iOS - API returns address as string!
  const formatAddress = (address: any): string | null => {
    if (!address) return null;
    
    // API returns address as a string already formatted
    if (typeof address === 'string') {
      return address; // Address is already a formatted string: "521 Red Drew Ave Tuscaloosa, AL 35401 US"
    }
    
    // Fallback: If it's an object, try to construct the address (legacy support)
    const street = address.street || address.address1 || address.line1;
    const city = address.city;
    const state = address.state || address.stateCode;
    const zipCode = address.zipCode || address.zip || address.postalCode;
    
    if (street && city && state && zipCode) {
      return `${street}, ${city}, ${state} ${zipCode}`;
    }
    
    return null;
  };

  if (!property) {
    return (
      <View style={styles.container}>
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>No property information available</Text>
        </View>
      </View>
    );
  }

  const formattedAddress = formatAddress(property.address);

  // Build sections array like iOS ContactOfficeView.updateSections()
  const buildSections = () => {
    const sections = [];
    
    // Header - if property has photo
    if (property.photoUrl) {
      sections.push({ type: 'header' });
    }
    // Website - if property has websites.home (iOS: property?.websites?.home)
    if (property.websites?.home) {
      sections.push({ 
        type: 'website', 
        icon: 'globe-outline',
        title: 'Website', // iOS: "home.contact_office.website".localized
        onPress: () => handleWebsitePress(property.websites!.home!),
        showArrow: true
      });
    }
    
    // Phone - if property has phone
    if (property.phone) {
      sections.push({ 
        type: 'phone', 
        icon: 'call-outline',
        title: formatAsPhoneNumber(property.phone), // iOS: property?.phone?.formatAsPhoneNumber()
        onPress: () => handlePhonePress(property.phone!),
        showArrow: true
      });
    }
    
    // Email - if property has email
    if (property.email) {
      sections.push({ 
        type: 'email', 
        icon: 'mail-outline',
        title: property.email,
        onPress: () => handleEmailPress(property.email!),
        showArrow: true
      });
    }
    
    // Address - if property has address (no arrow, non-interactive)
    if (formattedAddress) {
      sections.push({ 
        type: 'address', 
        icon: 'location-outline',
        title: formattedAddress,
        showArrow: false
      });
    }
    
    // Handbook - if property has websites.handbook or faqUrl
    if (property.websites?.handbook || property.faqUrl) {
      sections.push({ 
        type: 'handbook', 
        icon: 'book-outline',
        title: 'Resident Handbook', // iOS: "home.contact_office.handbook".localized
        onPress: () => handleResidentHandbookPress(),
        showArrow: true
      });
    }
    
    return sections;
  };

  const sections = buildSections();

  return (
    <ScrollView style={styles.container}>
      {sections.map((section, index) => {
        if (section.type === 'header') {
          return (
            <View key={section.type} style={styles.headerImageContainer}>
              <Image
                source={{ uri: property.photoUrl }}
                style={styles.headerImage}
                resizeMode="cover"
              />
            </View>
          );
        }
        
        return (
          <ContactInfoItem
            key={section.type}
            icon={section.icon!}
            title={section.title!}
            onPress={section.onPress}
            showArrow={section.showArrow!}
            isLast={index === sections.length - 1}
          />
        );
      })}
    </ScrollView>
  );
};

const makeStyles = ({ colors }: ThemeContextValue) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg.base, // iOS: .Background.primary (#F2F2F7)
  },
  headerImageContainer: {
    height: 300, // Same height as iOS ContactOfficeHeaderViewCell
    width: '100%',
  },
  headerImage: {
    width: '100%',
    height: '100%',
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.bg.base, // iOS: .Background.primary
    paddingVertical: 20, // iOS: .Padding.padding5 (top/bottom)
    paddingHorizontal: PADDING.padding4, // iOS: .Padding.padding4 (left/right)
    position: 'relative',
  },
  infoItemDisabled: {
    // Remove opacity change for non-interactive items
  },
  infoIconContainer: {
    width: 24, // iOS: .Padding.padding6
    height: 24, // iOS: .Padding.padding6
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: PADDING.padding3, // iOS: .Padding.padding3
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    fontSize: 13, // iOS: .primary(withSize: 13.0)
    fontWeight: '400', // iOS: .primary font
    color: colors.text.primary, // iOS: .Text.primary
  },
  infoSubtitle: {
    ...TYPOGRAPHY.cardDescription,
    color: colors.text.muted,
    fontSize: 14,
    marginTop: PADDING.paddingHalf,
  },
  separator: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: StyleSheet.hairlineWidth, // iOS: .BorderWidth.borderSmallest
    backgroundColor: colors.separator, // iOS: .Separator.primary
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: PADDING.padding8,
  },
  emptyText: {
    ...TYPOGRAPHY.cardDescription,
    color: colors.text.muted,
    textAlign: 'center',
  },
});